import {
  loadVideoProfiles,
  VIDEO_TYPES,
  videoProfileFor,
} from "./video-profiles";

const VIDEO_CODEC = "avc"; // H.264 — plays in browsers + iOS + Android
const AUDIO_CODEC = "aac";

// Loaded on demand: mediabunny is browser-only and ~1 MB, and a static import would
// pull it into the SSR graph for code only ever reached from a user gesture.
let mediabunnyPromise;
function loadMediabunny() {
  mediabunnyPromise ??= import("mediabunny");
  return mediabunnyPromise;
}

// Codecs and key-frame spacing are client concerns; everything else — the size cap,
// quality factor, frame rate and audio targets — comes from the server profile.
const CLIENT_PROFILE = {
  videoCodec: VIDEO_CODEC,
  audioCodec: AUDIO_CODEC,
  keyFrameInterval: 1, // seconds between key frames (re-encode path only)
};

/** The full target for a video type: server profile + the client-side codec choices. */
export function resolveProfile(type) {
  return { ...CLIENT_PROFILE, ...videoProfileFor(type) };
}

// Sample only a packet prefix for the fps/bitrate estimate (fast; no full scan).
const STATS_SAMPLE_PACKETS = 300;

// Discriminant on the transcodeVideo() result.
export const TranscodeOutcome = {
  TRANSCODED: "transcoded",
  DEFERRED: "deferred",
};

// Machine-readable reasons the client deferred to the server (for telemetry).
export const DeferReason = {
  NO_VIDEO_TRACK: "no-readable-video-track",
  NOT_DECODABLE: "input-not-decodable",
  VIDEO_ENCODER_UNAVAILABLE: "video-encoder-unavailable",
  AUDIO_ENCODER_UNAVAILABLE: "audio-encoder-unavailable",
  ABORTED: "aborted",
  ERROR: "mediabunny-error",
};

// TRANSCODED → file is a server-conformant faststart MP4 (finalize can copy it).
const transcoded = (file, meta) => ({
  outcome: TranscodeOutcome.TRANSCODED,
  file,
  meta,
  reason: null,
  detail: null,
});

// DEFERRED → upload the original; the server transcodes. `detail` carries the error.
const deferred = (file, reason, detail = null, meta = null) => ({
  outcome: TranscodeOutcome.DEFERRED,
  file,
  meta,
  reason,
  detail,
});

// Register the AAC encoder polyfill once, where the browser lacks native AAC. The
// polyfill is only fetched when it is actually needed.
let aacReady;
function ensureAacEncoder() {
  aacReady ??= (async () => {
    const { canEncodeAudio } = await loadMediabunny();
    if (await canEncodeAudio(AUDIO_CODEC)) return;
    const { registerAacEncoder } = await import("@mediabunny/aac-encoder");
    registerAacEncoder();
  })();
  return aacReady;
}

const encodeProbe = new Map();
// Can this browser encode `codec`? Installs the AAC polyfill on demand for AAC.
function canEncode(kind, codec) {
  const key = `${kind}:${codec}`;
  if (!encodeProbe.has(key)) {
    encodeProbe.set(
      key,
      (async () => {
        if (kind === "audio" && codec === AUDIO_CODEC) await ensureAacEncoder();
        const { canEncodeVideo, canEncodeAudio } = await loadMediabunny();
        return kind === "video" ? canEncodeVideo(codec) : canEncodeAudio(codec);
      })()
    );
  }
  return encodeProbe.get(key);
}

/** True if this browser can encode the default H.264/AAC targets. */
export function canTranscodeVideo() {
  return Promise.all([
    canEncode("video", VIDEO_CODEC),
    canEncode("audio", AUDIO_CODEC),
  ]).then(([video, audio]) => video && audio);
}

/** Read a file's primary-track metadata (no decode/transcode); null if no video
 *  track. One source of truth for the transcode decision and the upload finalize. */
export async function inspectVideo(file) {
  const { ALL_FORMATS, BlobSource, Input } = await loadMediabunny();
  const input = new Input({
    source: new BlobSource(file),
    formats: ALL_FORMATS,
  });
  try {
    return (await readTracks(input))?.meta ?? null;
  } finally {
    input.dispose?.();
  }
}

// Resolve the primary tracks + their metadata; null when there's no video track.
// Returns the live track handles too, so an open-input caller can sample packet stats.
async function readTracks(input) {
  const video = await input.getPrimaryVideoTrack();
  if (!video) return null;
  const audio = await input.getPrimaryAudioTrack();
  const [
    width,
    height,
    videoCodec,
    decodable,
    audioCodec,
    audioSampleRate,
    audioChannels,
  ] = await Promise.all([
    video.getDisplayWidth(),
    video.getDisplayHeight(),
    video.getCodec(), // 'avc' | 'hevc' | 'vp9' | ... | null
    video.canDecode(),
    audio ? audio.getCodec() : null,
    audio ? audio.getSampleRate() : null,
    audio ? audio.getNumberOfChannels() : null,
  ]);
  return {
    video,
    audio,
    meta: {
      width,
      height,
      videoCodec,
      audioCodec,
      audioSampleRate,
      audioChannels,
      hasAudio: Boolean(audio),
      decodable,
    },
  };
}

/** Normalize `file` to a faststart MP4: re-encode only what's off-profile, remux
 *  the rest (lossless). Never throws — defers to the server on any obstacle. */
export async function transcodeVideo(
  file,
  { type = VIDEO_TYPES.LIBRARY, onProgress, signal } = {}
) {
  if (signal?.aborted) return deferred(file, DeferReason.ABORTED);

  const {
    ALL_FORMATS,
    AppendOnlyStreamTarget,
    BlobSource,
    Conversion,
    Input,
    Mp4OutputFormat,
    Output,
  } = await loadMediabunny();

  // Refresh the server targets; cached and non-throwing.
  await loadVideoProfiles();
  const profile = resolveProfile(type);

  let input;
  let conversion;
  let onAbort;
  // Outside the try so a deferral thrown mid-conversion still reports the source
  // size, which the server needs to pick a matching bitrate.
  let sourceMeta = null;
  try {
    input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS });
    const tracks = await readTracks(input);
    if (!tracks) return deferred(file, DeferReason.NO_VIDEO_TRACK);
    const { video: videoTrack, meta } = tracks;
    sourceMeta = meta;

    const scaled = scaleToLongEdge(
      meta.width,
      meta.height,
      profile.maxLongEdge
    );
    const reencodeVideo = await videoNeedsReencode(
      videoTrack,
      meta,
      scaled,
      profile
    );
    const reencodeAudio = audioNeedsReencode(meta, profile);

    // Re-encoding needs a decoder + the matching encoder; copying needs neither.
    if (reencodeVideo) {
      if (!meta.decodable) {
        return deferred(file, DeferReason.NOT_DECODABLE, null, meta);
      }
      if (!(await canEncode("video", profile.videoCodec))) {
        // Common on Linux Chrome, which ships no WebCodecs H.264 encoder.
        return deferred(
          file,
          DeferReason.VIDEO_ENCODER_UNAVAILABLE,
          `${profile.videoCodec} encode unsupported`,
          meta
        );
      }
    }
    if (reencodeAudio && !(await canEncode("audio", profile.audioCodec))) {
      return deferred(
        file,
        DeferReason.AUDIO_ENCODER_UNAVAILABLE,
        `${profile.audioCodec} encode unsupported`,
        meta
      );
    }

    // 'fragmented' keeps metadata up front without holding the file in memory, and
    // writes sequentially — AppendOnlyStreamTarget throws if a format would seek.
    const chunks = [];
    const output = new Output({
      format: new Mp4OutputFormat({ fastStart: "fragmented" }),
      target: new AppendOnlyStreamTarget(
        // Copy: mediabunny may reuse the chunk's underlying buffer.
        new WritableStream({
          write: (chunk) => void chunks.push(chunk.slice()),
        })
      ),
    });
    const outWidth = scaled?.width ?? meta.width;
    const outHeight = scaled?.height ?? meta.height;
    // Resolve to bps here so the encode target matches the trigger exactly.
    const videoBitrate = qualityToVideoBitrate(
      profile.curve,
      profile.quality,
      profile.videoCodec,
      outWidth,
      outHeight
    );
    conversion = await Conversion.init({
      input,
      output,
      video: reencodeVideo
        ? {
            codec: profile.videoCodec,
            bitrate: videoBitrate,
            ...(Number.isFinite(profile.frameRate) &&
              profile.frameRate > 0 && { frameRate: profile.frameRate }),
            keyFrameInterval: profile.keyFrameInterval,
            // Required when both dimensions are set. The box already matches the
            // source aspect, so 'fill' pins exact even dimensions without padding.
            ...(scaled && { ...scaled, fit: "fill" }),
          }
        : {},
      audio: reencodeAudio
        ? {
            codec: profile.audioCodec,
            bitrate: profile.audioBitrate,
            sampleRate: profile.audioSampleRate,
            numberOfChannels: profile.audioChannels,
          }
        : {},
    });
    if (onProgress) conversion.onProgress = onProgress;
    if (signal) {
      onAbort = () => conversion.cancel();
      signal.addEventListener("abort", onAbort, { once: true });
    }
    await conversion.execute();

    const name = `${file.name.replace(/\.[^.]+$/, "") || "video"}.mp4`;
    return transcoded(new File(chunks, name, { type: "video/mp4" }), {
      width: outWidth,
      height: outHeight,
      remuxedOnly: !reencodeVideo && !reencodeAudio,
      droppedTracks: conversion.discardedTracks.map((track) => track.reason),
    });
  } catch (err) {
    // Never block the upload — defer to the server, surfacing the reason.
    if (signal?.aborted)
      return deferred(file, DeferReason.ABORTED, null, sourceMeta);
    return deferred(
      file,
      DeferReason.ERROR,
      err?.message ?? String(err),
      sourceMeta
    );
  } finally {
    if (signal && onAbort) signal.removeEventListener("abort", onAbort);
    input?.dispose?.();
  }
}

// The output no longer sits in RAM as one ArrayBuffer, but the input Blob and the
// decode buffers still do. Streaming chunks straight into the S3 POST would remove
// the last copy, and needs a presigned upload that accepts a ReadableStream body.

// Re-encode the video when its codec is off-target or it's oversized; an otherwise-
// conformant clip only when it's over budget (see exceedsVideoBudget).
async function videoNeedsReencode(videoTrack, meta, scaled, profile) {
  if (meta.videoCodec !== profile.videoCodec || scaled) return true;
  return exceedsVideoBudget(videoTrack, meta, profile);
}

// Over-budget on fps or on the resolution's target bitrate → re-encoding pays off.
// Stats are advisory: unreadable → false (keep the lossless copy).
async function exceedsVideoBudget(videoTrack, meta, profile) {
  const stats = await videoTrack
    .computePacketStats(STATS_SAMPLE_PACKETS)
    .catch(() => null);
  if (!stats) return false;
  if (stats.averagePacketRate > profile.frameRate) return true;
  const target = qualityToVideoBitrate(
    profile.curve,
    profile.quality,
    profile.videoCodec,
    meta.width,
    meta.height
  );
  return stats.averageBitrate > target;
}

// H.264-relative encode efficiency, mirroring mediabunny's Quality formula.
const CODEC_BITRATE_EFFICIENCY = {
  avc: 1.0,
  hevc: 0.6,
  vp9: 0.6,
  av1: 0.4,
  vp8: 1.2,
};

// Target bps for a w×h output. Same curve as msb-api media-convert.js and msb-app
// videoProfiles.js: 3 Mbps at 1080p/AVC, scaled by pixel count and codec.
function qualityToVideoBitrate(curve, quality, codec, width, height) {
  const reference = curve.referenceWidth * curve.referenceHeight;
  const scale = ((width * height) / reference) ** curve.exponent;
  const bps =
    curve.referenceBitrate *
    scale *
    (CODEC_BITRATE_EFFICIENCY[codec] ?? 1) *
    quality;
  return Math.ceil(bps / 1000) * 1000;
}

// Off-profile codec, sample rate or channel count all need a re-encode — checking
// only the codec let AAC mono/44.1kHz through untouched, diverging from the server.
function audioNeedsReencode(meta, profile) {
  if (!meta.hasAudio) return false;
  return (
    meta.audioCodec !== profile.audioCodec ||
    meta.audioSampleRate !== profile.audioSampleRate ||
    meta.audioChannels !== profile.audioChannels
  );
}

// Preserve aspect, cap the long edge, keep dimensions even; null = no downscale.
function scaleToLongEdge(w, h, maxLongEdge) {
  if (!w || !h) return null;
  const longEdge = Math.max(w, h);
  if (longEdge <= maxLongEdge) return null;
  const scale = maxLongEdge / longEdge;
  const even = (n) => Math.max(2, Math.round((n * scale) / 2) * 2);
  return { width: even(w), height: even(h) };
}
